import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Package, FileText, Truck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTrackPackage = ({ token, clients, onNavigate }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const axiosConfig = { headers: { Authorization: `Bearer ${token}` } };

  const handleSearch = async () => {
    if (!trackingNumber.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      // Search tracking
      const trackRes = await axios.get(`${API}/admin/trackings`, axiosConfig);
      const tracking = trackRes.data.find(t => 
        t.tracking_number?.toLowerCase() === trackingNumber.trim().toLowerCase()
      );

      if (!tracking) {
        setError('Tracking no encontrado en el sistema');
        setLoading(false);
        return;
      }

      // Find client
      const client = clients?.find(c => c.id === tracking.client_id);

      // Find WR containing this tracking
      const wrRes = await axios.get(`${API}/admin/warehouse-receipts`, axiosConfig);
      const wr = wrRes.data.find(w => 
        w.tracking_numbers?.includes(tracking.tracking_number) ||
        w.tracking_ids?.includes(tracking.id)
      );

      // Find invoice for this WR or client
      let invoice = null;
      if (wr) {
        const invRes = await axios.get(`${API}/admin/invoices`, axiosConfig);
        invoice = invRes.data.find(inv => 
          inv.warehouse_receipt_id === wr.id ||
          inv.guias?.includes(wr.wr_number)
        );
      }

      setResult({ tracking, client, wr, invoice });
    } catch (err) {
      setError('Error al buscar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-track-package">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search size={22} />
            Rastrear Paquete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Ingrese número de tracking..."
              className="flex-1"
              data-testid="track-input"
            />
            <Button onClick={handleSearch} disabled={loading} data-testid="track-search-btn">
              {loading ? 'Buscando...' : 'Rastrear'}
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4" data-testid="track-error">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-4" data-testid="track-results">
              {/* Tracking Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                  <Package size={18} /> Paquete
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Tracking:</span> <span className="font-mono font-bold">{result.tracking.tracking_number}</span></div>
                  <div><span className="text-gray-500">Carrier:</span> {result.tracking.carrier || '-'}</div>
                  <div><span className="text-gray-500">Descripción:</span> {result.tracking.description || '-'}</div>
                  <div><span className="text-gray-500">Estado:</span> <span className={`font-medium ${result.tracking.consolidated ? 'text-green-600' : 'text-yellow-600'}`}>{result.tracking.consolidated ? 'Procesado' : 'Pendiente'}</span></div>
                  <div>
                    <span className="text-gray-500">Cliente:</span>{' '}
                    {result.client ? (
                      <button onClick={() => onNavigate('clients', { filterClientId: result.client.id })} className="text-blue-600 hover:underline font-medium">
                        {result.client.full_name} ({result.client.locker_number || result.client.email})
                      </button>
                    ) : <span className="text-gray-400">No asignado</span>}
                  </div>
                </div>
              </div>

              {/* WR Info */}
              <div className={`border rounded-lg p-4 ${result.wr ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className={`font-semibold flex items-center gap-2 mb-3 ${result.wr ? 'text-green-800' : 'text-gray-500'}`}>
                  <Truck size={18} /> Warehouse Receipt
                </h3>
                {result.wr ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">WR:</span>{' '}
                      <button onClick={() => onNavigate('warehouse', { searchWr: result.wr.wr_number })} className="text-green-700 hover:underline font-bold">
                        {result.wr.wr_number}
                      </button>
                    </div>
                    <div><span className="text-gray-500">Peso:</span> {result.wr.final_weight_lb || result.wr.weight_lb || '-'} lb</div>
                    <div><span className="text-gray-500">Estado:</span> {result.wr.status || '-'}</div>
                    <div><span className="text-gray-500">Trackings:</span> {result.wr.tracking_numbers?.length || 0}</div>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Este paquete aún no tiene Warehouse Receipt</p>
                )}
              </div>

              {/* Invoice Info */}
              <div className={`border rounded-lg p-4 ${result.invoice ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                <h3 className={`font-semibold flex items-center gap-2 mb-3 ${result.invoice ? 'text-yellow-800' : 'text-gray-500'}`}>
                  <FileText size={18} /> Factura
                </h3>
                {result.invoice ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Factura:</span>{' '}
                      <button onClick={() => onNavigate('invoices', { searchWr: result.invoice.invoice_number })} className="text-yellow-700 hover:underline font-bold">
                        {result.invoice.invoice_number}
                      </button>
                    </div>
                    <div><span className="text-gray-500">Total:</span> <span className="font-bold">${result.invoice.total?.toFixed(2) || '0.00'}</span></div>
                    <div><span className="text-gray-500">Estado:</span> {result.invoice.status || '-'}</div>
                    <div><span className="text-gray-500">Fecha:</span> {result.invoice.created_at ? new Date(result.invoice.created_at).toLocaleDateString() : '-'}</div>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Este paquete aún no tiene factura asociada</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTrackPackage;
